<template>
  <div v-if="isInitialLoading" class="my-bookings__skeleton grid-layout">
    <div class="my-bookings__main grid-layout__main">
      <div class="my-bookings__skeleton-card" v-for="n in 3" :key="n">
        <SkeletonLoader type="rectangle" height="120px" />
      </div>
    </div>
  </div>
  
  <TabContainer
    v-else
    :tabs="tabs"
    v-model="activeTab"
    :hasTwoContents="true"
    :tabOneText="$t('myPage.bookings.future')"
    :tabTwoText="$t('myPage.bookings.past')"
  >
    <template #content1>
      <section class="my-bookings grid-layout">
        <div class="my-bookings__main grid-layout__main">
          <MyPageShoutOut />
          <div
            v-if="(isLoading || isLoadingMore || !hasFetchedFutureBookings) && !futureBookings.length"
            class="my-bookings__loading"
          >
            {{ $t('myPage.bookings.loading') }}
          </div>
          <template v-else-if="futureBookings.length">
            <TicketCardContainer
              v-for="booking in futureBookings"
              :key="booking.booking_number"
              :bookingNumber="booking.booking_number"
              :bookingData="booking"
            />
          </template>
          <p
            v-else-if="!isLoading && !isLoadingMore && hasFetchedFutureBookings"
            class="my-bookings__empty"
          >
            {{ $t('myPage.bookings.noFutureBookings') }}
          </p>
          <button
            v-if="nextPage && !isLoading && !isLoadingMore && activeTab === 1"
            @click="loadMore"
            class="my-bookings__button my-bookings__button--load-more button button--medium button--primary"
            :disabled="isLoading || isLoadingMore"
          >
            {{ $t('myPage.bookings.loadMore') }}
          </button>
          <div
            v-if="isLoadingMore"
            class="my-bookings__loading my-bookings__loading--more"
          >
            {{ $t('myPage.bookings.loadingMore') }}
          </div>
        </div>
      </section>
    </template>
    <template #content2>
      <section class="my-bookings grid-layout">
        <div class="my-bookings__main grid-layout__main">
          <div
            v-if="(isLoading || isLoadingMore || !hasFetchedPastBookings) && !pastBookings.length"
            class="my-bookings__loading"
          >
            {{ $t('myPage.bookings.loading') }}
          </div>
          <template v-else-if="pastBookings.length">
            <TicketCardContainer
              v-for="booking in pastBookings"
              :key="booking.booking_number"
              :bookingNumber="booking.booking_number"
              :bookingData="booking"
            />
          </template>
          <p
            v-else-if="!isLoading && !isLoadingMore && hasFetchedPastBookings"
            class="my-bookings__empty"
          >
            {{ $t('myPage.bookings.noPastBookings') }}
          </p>
          <button
            v-if="
              pastNextPage && !isLoading && !isLoadingMore && activeTab === 2
            "
            @click="loadMore"
            class="my-bookings__button my-bookings__button--load-more button button--medium button--primary"
            :disabled="isLoading || isLoadingMore"
          >
            {{ $t('myPage.bookings.loadMore') }}
          </button>
          <div
            v-if="isLoadingMore"
            class="my-bookings__loading my-bookings__loading--more"
          >
            {{ $t('myPage.bookings.loadingMore') }}
          </div>
        </div>
      </section>
    </template>
  </TabContainer>

  <p
    v-if="errorMessage"
    class="my-bookings__error"
  >
    {{ errorMessage }}
  </p>
  </template>

<script lang="ts">
  import { defineComponent, ref, computed, watch, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useMyBookingsStore } from '@/stores/myBookingsStore';
  import TabContainer from '@/components/BaseComponents/TabContainer/TabContainer.vue';
  import TicketCardContainer from './TicketCardContainer.vue';
  import MyPageShoutOut from './MyPageShoutOut.vue';
  import SkeletonLoader from '@/components/Common/SkeletonLoader.vue';

  export default defineComponent({
    name: 'MyBookings',
    components: {
      TabContainer,
      TicketCardContainer,
      MyPageShoutOut,
      SkeletonLoader,
    },
    setup() {
      const { t } = useI18n();
      const myBookingsStore = useMyBookingsStore();

      const futureBookings = computed(() => myBookingsStore.getFutureBookings);
      const pastBookings = computed(() => myBookingsStore.getPastBookings);
      const isLoading = computed(() => myBookingsStore.getIsLoading);
      const isLoadingMore = computed(() => myBookingsStore.getIsLoadingMore);
      const errorMessage = computed(() => myBookingsStore.getErrorMessage);
      const nextPage = computed(() => myBookingsStore.getHasFutureNextPage);
      const pastNextPage = computed(() => myBookingsStore.getHasPastNextPage);
      const hasFetchedFutureBookings = computed(() => myBookingsStore.hasFetchedFutureBookings);
      const hasFetchedPastBookings = computed(() => myBookingsStore.hasFetchedPastBookings);

      const isInitialLoading = computed(() => {
        return isLoading.value && !hasFetchedFutureBookings.value && activeTab.value === 1;
      });

      const activeTab = ref(1);
      const tabs = ref([
        { id: 1, title: t('myPage.bookings.future') },
        { id: 2, title: t('myPage.bookings.past') },
      ]);

      const formatScheduleDate = (date: string) => {
        if (!date) return '';
        const options: Intl.DateTimeFormatOptions = {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: 'Europe/Oslo',
        };
        return new Date(date).toLocaleDateString('nb-NO', options) + ' ';
      };

      const formatTime = (timestamp: string) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleTimeString('nb-NO', {
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Europe/Oslo',
        });
      };

      const loadMore = async () => {
        if (!isLoading.value && !isLoadingMore.value) {
          if (activeTab.value === 1) {
            const nextPageUrl = myBookingsStore.$state.nextPage || undefined;
            await myBookingsStore.loadFutureBookings(nextPageUrl);
          } else {
            const pastNextPageUrl = myBookingsStore.$state.pastNextPage || undefined;
            await myBookingsStore.loadPastBookings(pastNextPageUrl);
          }
        }
      };

      watch(
        () => activeTab.value,
        async (newTab) => {
          myBookingsStore.clearErrorMessage();
          if (newTab === 2) {
            if (!myBookingsStore.hasFetchedPastBookings && !isLoading.value) {
              await myBookingsStore.loadPastBookings();
              if (myBookingsStore.pastBookings.length > 0) {
                const bookingsToFetch = myBookingsStore.pastBookings
                  .slice(0, 5)
                  .map(booking => booking.booking_number);
                await myBookingsStore.fetchMultipleBookingDetails(bookingsToFetch);
              }
            }
          } else if (newTab === 1) {
            if (!myBookingsStore.hasFetchedFutureBookings && !isLoading.value) {
              await myBookingsStore.loadFutureBookings();
              if (myBookingsStore.futureBookings.length > 0) {
                const bookingsToFetch = myBookingsStore.futureBookings
                  .slice(0, 5)
                  .map(booking => booking.booking_number);
                await myBookingsStore.fetchMultipleBookingDetails(bookingsToFetch);
              }
            }
          }
        },
        { immediate: true }
      );

      onMounted(async () => {
        await myBookingsStore.loadFutureBookings();
        if (myBookingsStore.futureBookings.length > 0) {
          const bookingsToFetch = myBookingsStore.futureBookings
            .slice(0, 5)
            .map(booking => booking.booking_number);
          await myBookingsStore.fetchMultipleBookingDetails(bookingsToFetch);
        }
      });

      const isBookingCancelled = (booking: any) => {
        return booking.meta_data?.cancel_datetime ? true : false;
      };

      const hasBookingDetails = (bookingIdentifier: string) => {
        const status = myBookingsStore.getBookingDetailsStatus(bookingIdentifier);
        return status.exists;
      };

      const isLoadingBookingDetails = (bookingIdentifier: string) => {
        const status = myBookingsStore.getBookingDetailsStatus(bookingIdentifier);
        return status.isLoading;
      };

      return {
        futureBookings,
        pastBookings,
        isLoading,
        isLoadingMore,
        errorMessage,
        activeTab,
        tabs,
        loadMore,
        formatScheduleDate,
        formatTime,
        isBookingCancelled,
        hasBookingDetails,
        isLoadingBookingDetails,
        nextPage,
        pastNextPage,
        hasFetchedFutureBookings,
        hasFetchedPastBookings,
        isInitialLoading,
      };
    },
  });
</script>


<style lang="scss" scoped>
  @import '@/styles/grid.scss';

  .my-bookings {
    &__main {
      display: flex;
      flex-direction: column;
      gap: 32px;
    }
    
    &__skeleton {
      margin-top: 1rem;
      
      &-card {
        margin-bottom: 1.5rem;
        border-radius: 8px;
        overflow: hidden;
      }
    }

    &__empty {
      text-align: center;
      color: $clr-primary-800;
      margin: 2rem 0;
    }

    &__loading {
      text-align: center;
      padding: 1rem;

      &--more {
        margin-top: 1rem;
      }
    }

    &__error {
      text-align: center;
      padding: 1rem;
      color: $clr-error;
    }

    &__button {
      display: block;
      margin: 2rem auto;

      &--load-more {
        min-width: 200px;
        margin-top: 1rem;

        &:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
      }
    }

    &__search-card {
      padding: 2rem;
      text-align: center;

      h2 {
        color: $clr-primary;
        margin-bottom: 1rem;
      }

      p {
        margin-bottom: 1.5rem;
        color: $clr-primary-800;
      }
    }

    &__search-button {
      width: 100%;
    }
  }
</style>
